import { useState } from 'react';
import { EMAIL_VALIDE, adresseDeLaFiche } from '../lib/emails-offre';
import { tr } from '../lib/i18n';
import { C } from '../theme/palette';
import { _inp } from './ui/Inputs.jsx';

const puce = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  border: '1.5px solid ' + C.teal,
  background: C.tealL,
  borderRadius: 10,
  padding: '7px 10px',
  marginBottom: 6,
  fontSize: 11,
  color: C.ink,
};

/**
 * « 📧 Envoyer par e-mail à » sur l'offre, sous les coordonnées du client :
 * l'adresse de la fiche, et les autres personnes de la société (le patron,
 * le technicien…). Un seul e-mail part à toutes ; chacune voit à qui l'offre
 * a été envoyée.
 *
 * @param {object} props propriétés
 * @param {string[]} [props.value] autres adresses de l'offre
 * @param {(v: string[]) => void} props.onChange reçoit la nouvelle liste
 * @param {object} [props.client] client choisi
 * @param {string} [props.adresseFiche] adresse de la fiche à montrer (celle des factures pour une facture)
 * @returns {import('react').ReactElement} le bloc
 */
export function EmailsOffre({ value, onChange, client, adresseFiche }) {
  const [saisie, setSaisie] = useState('');
  const [erreur, setErreur] = useState('');
  const autres = value || [];
  const fiche = adresseFiche ?? adresseDeLaFiche(client);
  const deja = (a) =>
    [fiche, ...autres].some((x) => x && x.toLowerCase() === String(a).trim().toLowerCase());
  const ajoute = (a) => {
    const adresse = String(a || '').trim();
    if (!EMAIL_VALIDE.test(adresse)) {
      setErreur(tr('L’adresse e-mail est invalide.'));
      return;
    }
    if (!deja(adresse)) onChange([...autres, adresse]);
    setSaisie('');
    setErreur('');
  };
  const suggestions = (client?.contacts || []).filter((p) => p.email && !deja(p.email));
  const tous = (fiche ? 1 : 0) + autres.length;

  return (
    <div style={{ marginBottom: 10 }}>
      <div
        style={{
          fontSize: 9,
          fontWeight: 700,
          color: C.muted,
          marginBottom: 5,
          textTransform: 'uppercase',
          letterSpacing: 0.4,
        }}
      >
        📧 {tr('Envoyer par e-mail à')}
      </div>
      {fiche ? (
        <div style={puce}>
          <span style={{ color: C.teal, fontWeight: 900 }}>✓</span>
          <span style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{fiche}</span>
          <span style={{ fontSize: 9, color: C.muted, fontWeight: 700 }}>
            {tr('Fiche du client')}
          </span>
        </div>
      ) : (
        <div style={{ fontSize: 10, color: C.muted, fontStyle: 'italic', marginBottom: 6 }}>
          {tr('La fiche du client n’a pas d’adresse e-mail.')}
        </div>
      )}
      {autres.map((a) => (
        <div key={a} style={puce}>
          <span style={{ color: C.teal, fontWeight: 900 }}>✓</span>
          <span style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{a}</span>
          <button
            type="button"
            aria-label={tr('Retirer') + ' ' + a}
            onClick={() => onChange(autres.filter((x) => x !== a))}
            style={{
              border: 'none',
              background: 'none',
              color: C.red,
              fontSize: 12,
              cursor: 'pointer',
              width: 26,
              height: 26,
            }}
          >
            ✕
          </button>
        </div>
      ))}
      {suggestions.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, margin: '2px 0 8px' }}>
          {suggestions.map((p) => (
            <button
              key={p.email}
              type="button"
              onClick={() => ajoute(p.email)}
              style={{
                border: '1.5px dashed ' + C.teal,
                background: C.white,
                color: C.teal,
                borderRadius: 99,
                padding: '5px 10px',
                fontSize: 10,
                fontWeight: 800,
                cursor: 'pointer',
              }}
            >
              ＋ {p.nom}
              {p.fonction ? ' · ' + p.fonction : ''}
            </button>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: 6 }}>
        <input
          type="email"
          aria-label={tr('Autre adresse e-mail')}
          value={saisie}
          placeholder={tr('Autre adresse (technicien, patron…)')}
          onChange={(e) => setSaisie(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              ajoute(saisie);
            }
          }}
          style={{ ..._inp, flex: 1 }}
        />
        <button
          type="button"
          onClick={() => ajoute(saisie)}
          disabled={!saisie.trim()}
          style={{
            border: 'none',
            borderRadius: 9,
            padding: '0 12px',
            background: saisie.trim() ? C.teal : C.border,
            color: '#fff',
            fontSize: 11,
            fontWeight: 800,
            cursor: saisie.trim() ? 'pointer' : 'default',
            whiteSpace: 'nowrap',
          }}
        >
          ＋ {tr('Ajouter')}
        </button>
      </div>
      {erreur && (
        <div role="alert" style={{ fontSize: 10.5, color: C.red, marginTop: 4 }}>
          {erreur}
        </div>
      )}
      {tous > 1 && (
        <div style={{ fontSize: 10, color: C.muted, marginTop: 5, lineHeight: 1.4 }}>
          {tr('Un seul e-mail part à toutes ces adresses : chacun voit à qui il a été envoyé.')}
        </div>
      )}
    </div>
  );
}
